import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs } from 'antd';
import PropTypes from 'prop-types';

import TableAddDrawer from './TableAddDrawer';
import { includesTerm } from '../../../helpers/helpers';
import { getMaterials } from '../../../materials/state/materials.actions';
import MaterialSelect from '../../../materials/MaterialSelect';
import useValidateMaxSelect from '../../../common/hooks/useValidateMaxSelect';
import { getMaterialPath } from '../../../materials/materialsHelpers';

const { TabPane } = Tabs;

const PROJECT_TAB = 'project';
const GLOBAL_TAB = 'global';

export default function MaterialTableAddDrawer({
  visible,
  onClose,
  onSubmit,
  readOnly = false,
  selected: initialSelected = [],
  showInput,
  materials: materialData,
  projectMaterials,
  existingMaterials = [],
  maxExistingAllowed,
  zIndex,
  title = 'Add Materials',
}) {
  const dispatch = useDispatch();
  const materials = useSelector((state) => materialData || state.materials.materials);
  const [searchStr, setSearchStr] = useState();
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [activeTab, setActiveTab] = useState(PROJECT_TAB);

  const hasProjectMaterials = !!projectMaterials?.length;

  const selectedRowKeys = useMemo(() => selected.map((s) => s.id), [selected]);

  const onSubmitClicked = useCallback(() => {
    const relevantSelected = showInput ? selected : selectedRowKeys;

    const isValid = useValidateMaxSelect({
      maxExistingAllowed,
      selected: relevantSelected,
      type: 'material(s)',
    });
    if (isValid) onSubmit(relevantSelected);
  }, [
    selected,
    selectedRowKeys,
    showInput,
    onSubmit,
    maxExistingAllowed,
  ]);

  const projectMaterialsMap = useMemo(() => {
    if (!hasProjectMaterials) return {};
    const map = {};
    projectMaterials.forEach((material) => {
      map[material.id] = {
        ...material,
        isProjectMaterial: true,
        groupId: null,
        active: 1,
      };
    });
    return map;
  }, [projectMaterials, hasProjectMaterials]);

  const materialsMap = useMemo(() => ({
    ...materials,
    ...projectMaterialsMap,
  }), [materials, projectMaterialsMap]);

  useEffect(() => {
    if (initialSelected) {
      const initSelected = [];
      initialSelected.forEach((material) => {
        const relevantMaterial = materialsMap[material?.id || material];

        if (!relevantMaterial) {
          return;
        }

        initSelected.push({
          ...relevantMaterial,
          quantity: material.quantity,
        });
      });

      setSelected(initSelected);
    }
  }, [initialSelected, materialsMap]);

  useEffect(() => {
    if (!visible) {
      setSearchStr();
      setActiveTab(PROJECT_TAB);
      setSelected([]);
    }
  }, [visible]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await dispatch(getMaterials());
      setLoading(false);
    };
    if (visible && dispatch) load();
  }, [visible]);

  const selectedIds = useMemo(() => new Set(selected.map((item) => item.id)), [selected]);
  const hiddenMaterialIds = useMemo(() => {
    const hidden = new Set();
    
    // materials selected in the drawer
    selected.forEach((item) => {
      if (item.globalMaterialId) hidden.add(item.globalMaterialId);
    });

    // hide project rows whose global material is already selected
    projectMaterials?.forEach((projectMaterial) => {
      const linkedGlobalId = projectMaterial.globalMaterialId;
      if (!linkedGlobalId || selectedIds.has(projectMaterial.id)) return;
      if ((hidden.has(linkedGlobalId) || selectedIds.has(linkedGlobalId))) {
        hidden.add(projectMaterial.id);
      }
    });

    return hidden;
  }, [existingMaterials, selected, selectedIds, projectMaterials]);

  const globalMaterialsList = useMemo(() => (
    Object.values(materials)
      .filter(({ active, id }) => active && !hiddenMaterialIds.has(id))
      .map((material) => ({
        ...material,
        ...getMaterialPath(material, materials),
      }))
  ), [materials, hiddenMaterialIds]);

  const projectMaterialsList = useMemo(() => {
    if (!hasProjectMaterials) return [];
    return projectMaterials
      .filter((projectMaterial) => !hiddenMaterialIds.has(projectMaterial.id))
      .map((projectMaterial) => ({
        ...projectMaterial,
        isProjectMaterial: true,
        groupId: null,
        active: 1,
      }));
  }, [projectMaterials, hasProjectMaterials, hiddenMaterialIds]);

  const filterBySearch = useCallback((list) => {
    if (!searchStr) return list;
    return list.filter((material) => (
      includesTerm(material.name, searchStr)
      || includesTerm(material.description, searchStr)
    ));
  }, [searchStr]);

  const globalData = useMemo(
    () => filterBySearch(globalMaterialsList),
    [filterBySearch, globalMaterialsList],
  );

  const projectData = useMemo(
    () => filterBySearch(projectMaterialsList),
    [filterBySearch, projectMaterialsList],
  );

  const materialSelect = (data, map) => (
    <MaterialSelect
      data={data}
      selected={selected}
      setSelected={setSelected}
      loading={loading}
      readOnly={readOnly}
      showInput={showInput}
      maxExistingAllowed={maxExistingAllowed}
      searchStr={searchStr}
      materialsMap={map}
    />
  );

  return (
    <TableAddDrawer
      title={title}
      visible={visible}
      onClose={onClose}
      onSearch={setSearchStr}
      onSubmit={onSubmitClicked}
      searchStr={searchStr}
      destroyOnClose
      zIndex={zIndex}
    >
      {hasProjectMaterials ? (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
        >
          <TabPane tab="Project" key={PROJECT_TAB}>
            {materialSelect(projectData, projectMaterialsMap)}
          </TabPane>
          <TabPane tab="Global" key={GLOBAL_TAB}>
            {materialSelect(globalData, materials)}
          </TabPane>
        </Tabs>
      ) : (
        materialSelect(globalData, materials)
      )}
    </TableAddDrawer>
  );
}

MaterialTableAddDrawer.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  readOnly: PropTypes.bool,
  selected: PropTypes.arrayOf(PropTypes.string),
  showInput: PropTypes.bool,
  materials: PropTypes.shape(PropTypes.shape),
  projectMaterials: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  existingMaterials: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    materialId: PropTypes.string,
    globalMaterialId: PropTypes.string,
  })),
  maxExistingAllowed: PropTypes.number,
  zIndex: PropTypes.number,
  title: PropTypes.string,
};

MaterialTableAddDrawer.defaultProps = {
  visible: false,
  onClose: () => {},
  onSubmit: () => {},
  readOnly: false,
  selected: [],
  showInput: false,
  materials: null,
  projectMaterials: undefined,
  existingMaterials: [],
  maxExistingAllowed: null,
  zIndex: undefined,
  title: 'Add Materials',
};
